import {
  MeContextBasicQuery,
  SupplierOrderBy,
  TourOrderBy,
} from "@graphql/types";
import { graphql } from "@helpers/graphql";
import { GetStaticPropsResult } from "next";
import { PostOrderBy } from "../../../graphql/types";
import { isDecimal } from "@helpers/numbers";

interface LoaderParams {
  id: number;
  auth?: MeContextBasicQuery;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const loader_SupplierById = async ({
  id,
  auth,
}: LoaderParams): Promise<GetStaticPropsResult<Props>> => {
  const supplierId = id;

  if (isNaN(supplierId)) {
    return {
      notFound: true,
    };
  }

  if (isDecimal(supplierId)) {
    return {
      notFound: true,
    };
  }

  const supplier = await graphql.SupplierByIdWithVideoThumbnail({
    id: supplierId,
    badgeFilters: {
      ACTIVE_BADGES: true,
    },
  });

  if (!supplier.supplierById) {
    return {
      notFound: true,
    };
  }

  const tours = await graphql.tours(getToursVariables(supplierId));
  const posts = await graphql.posts(getPostsVariables(supplierId));
  const reviews = await graphql.reviews(getReviewsVariables(supplierId));
  const suppliers = await graphql.suppliers({
    filters: {
      ...(supplier.supplierById?.city?.id && {
        CITIES_IDS: [supplier.supplierById?.city?.id],
      }),
      EXCLUDE_IDS: [supplierId],
      ORDER_BY: SupplierOrderBy.Recommended,
    },
    page: 1,
    sizePerPage: 4,
  });

  // If supplier is offline, only admin or owner can view the page
  const isOnline = supplier.supplierById?.isOnline;
  const isBan = supplier.supplierById?.isBan;
  if (isOnline === false || isBan === true) {
    if (!auth) {
      return {
        notFound: true,
      };
    }

    const isAdmin = auth.me.isAdmin;
    const isOwner = auth.me.id === supplierId;

    if (!isAdmin && !isOwner) {
      return {
        notFound: true,
      };
    }
  }

  return {
    props: {
      initialData: {
        supplier,
        tours,
        posts,
        reviews,
        suppliers,
      },
    },
  };
};

export const getToursVariables = (supplierId: number) => {
  return {
    page: 1,
    sizePerPage: 6,
    filters: {
      SUPPLIERS_IDS: [supplierId],
      ORDER_BY: TourOrderBy.Recommended,
    },
  };
};

export const getPostsVariables = (
  supplierId: number,
  pageSize?: number,
  orderBy?: PostOrderBy
) => {
  return {
    first: pageSize || 4,
    filters: {
      USERS_IDS: [supplierId],
      ORDER_BY: orderBy,
    },
  };
};

export const getReviewsVariables = (supplierId: number) => {
  return {
    first: 3,
    filters: {
      SUPPLIERS_IDS: [supplierId],
    },
  };
};

export const loader_BlogWriterById = async ({
  id,
  auth,
}: LoaderParams): Promise<GetStaticPropsResult<Props>> => {
  const blogWriterId = id;

  if (isNaN(blogWriterId)) {
    return {
      notFound: true,
    };
  }

  try {
    const blogWriter = await graphql.blogWriterById({
      id: blogWriterId,
    });

    if (!blogWriter.blogWriterById) {
      return {
        notFound: true,
      };
    }

    // If supplier is offline, only admin or owner can view the page

    const isBan = blogWriter.blogWriterById?.isBan;
    if (isBan === true || !blogWriter.blogWriterById?.isBlogger) {
      if (!auth) {
        return {
          notFound: true,
        };
      }
    }

    if (blogWriter.blogWriterById?.isSupplier === true) {
      return {
        redirect: {
          destination: `/supplier/${blogWriter.blogWriterById?.id}`,
          statusCode: 301,
        },
      };
    }

    const popularPostsByViews = await graphql.posts(
      getPostsVariables(blogWriterId, 4, PostOrderBy.RankingDesc)
    );

    const latestBlogs = await graphql.posts(
      getPostsVariables(blogWriterId, 8, PostOrderBy.Latest)
    );

    return {
      props: {
        initialData: {
          blogWriter,
          popularPostsByViews,
          latestBlogs,
        },
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};
